import type { RxJsonSchema } from 'rxdb'

/**
 * Notification Document Type (Denormalized for RxDB)
 */
export interface NotificationDocument {
  id: string
  envId: string
  userId: string
  entityType: string | null
  entityId: string | null
  type: string // Changed from notificationType to match backend
  title: string
  message: string | null
  actionUrl: string | null
  isRead: boolean
  readAt: number | null
  priority: number // Added to match backend
  reminderId: string | null // Added to match backend
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Notification
 */
export const notificationSchema: RxJsonSchema<NotificationDocument> = {
  version: 2,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    entityId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    type: {
      type: 'string',
      maxLength: 50,
    },
    title: {
      type: 'string',
      maxLength: 255,
    },
    message: {
      type: ['string', 'null'],
    },
    actionUrl: {
      type: ['string', 'null'],
      maxLength: 500,
    },
    isRead: {
      type: 'boolean',
      default: false,
    },
    readAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    priority: {
      type: 'number',
      minimum: 0,
      maximum: 3,
      multipleOf: 1,
      default: 1,
    },
    reminderId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'userId',
    'type',
    'title',
    'isRead',
    'priority',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'userId',
    'updatedOn',
    'createdOn',
    ['userId', 'isRead'],
    ['userId', 'isDeleted'],
  ],
}

// Idempotent strip of the phantom denormalization fields. Used by both v0→v1
// and v1→v2 because some dev IndexedDBs ended up at v1 with the phantoms
// still in storage (the field set + hash existed before the strip landed).
// Either start point — v0-with-phantoms or v1-with-phantoms — converges here.
const stripTriggeredBy = (oldDoc: any): NotificationDocument => {
  const { triggeredByName, triggeredByEmail, ...rest } = oldDoc
  return rest as NotificationDocument
}

export const notificationMigrationStrategies = {
  // v0 -> v1: strip phantom `triggeredByName` + `triggeredByEmail` (backend
  // `NotificationSyncDocument` has no such columns).
  1: stripTriggeredBy,
  // v1 -> v2: re-strip for dev IndexedDBs that were created at v1 BEFORE the
  // strip migration existed (DB6 fallout from the schema-hash-vs-version
  // mismatch the previous iteration caused). No-op for clean v1 docs.
  2: stripTriggeredBy,
}
